import React, { Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { createOrder } from "../../actions/orderAction";
import { orderCompleted } from '../../slices/cartSlice';
import Swal from "sweetalert2";
import { useNavigate, Link } from 'react-router-dom';
import CheckoutSteps from './CheckoutSteps';

export const Orderconfirm = () => {
    const { shippingInfo, items: cartItems } = useSelector((state) => state.cartState)
    const { user } = useSelector((state) => state.authState)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const calculateItemPrice = (item) => {
        return (item.price / 8 * item.weight) + (item.price * item.makingrate / 100);
    };

    const totalPrice = cartItems.reduce((acc, item) => (
        acc + item.quantity * calculateItemPrice(item)
    ), 0);

    const handleClick = () => {
        try {
            const order = {
                orderItems: cartItems,
                shippingInfo,
                user: user._id,
                totalPrice
            };
            dispatch(createOrder(order))

            Swal.fire({
                icon: "success",
                title: "Order Placed Successfully",
                text: "Your luxury collection is being prepared for shipment.",
                confirmButtonColor: '#D4AF37'
            });
            
            navigate('/')
            dispatch(orderCompleted())
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Processing Failed",
                text: "Something went wrong during order creation.",
                confirmButtonColor: '#D4AF37'
            });
        }
    }

    return (
        <Fragment>
            <div className="bg-white min-h-screen pt-24 pb-32">
                <div className="container mx-auto px-4">
                    {/* Header & Steps */}
                    <div className="max-w-4xl mx-auto mb-16">
                        <h1 className="text-4xl lg:text-5xl font-serif font-bold text-center text-gray-900 mb-8 whitespace-nowrap overflow-hidden text-ellipsis">Final Review</h1>
                        <CheckoutSteps shipping confirmOrder />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
                        {/* Info Section */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Shipping info card */}
                            <div className="bg-gray-50/50 rounded-3xl p-8 border border-gray-100 shadow-sm transition-all hover:bg-white hover:shadow-md">
                                <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-4">
                                    <h3 className="text-xs font-bold tracking-[.3em] text-yellow-600 uppercase">Delivery Address</h3>
                                    <Link to="/shipping" className="text-[10px] font-bold text-gray-400 hover:text-yellow-600 uppercase tracking-widest">Edit Details</Link>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 text-sm">
                                    <div className="space-y-1">
                                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Recipient</div>
                                        <div className="text-gray-900 font-bold">{shippingInfo.fullname}</div>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Phone / Email</div>
                                        <div className="text-gray-900 font-bold">{shippingInfo.phone_No}</div>
                                        <div className="text-gray-600 italic text-xs">{shippingInfo.email}</div>
                                    </div>
                                    <div className="sm:col-span-2 space-y-1 mt-2">
                                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Street Address</div>
                                        <div className="text-gray-900 font-medium">
                                            {shippingInfo.house_No}, {shippingInfo.street}<br />
                                            {shippingInfo.city}, {shippingInfo.district} - {shippingInfo.postalCode}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Order Review card */}
                            <div className="bg-gray-50/50 rounded-3xl p-8 border border-gray-100 shadow-sm">
                                <h3 className="text-xs font-bold tracking-[.3em] text-gray-900 uppercase mb-8">Selected Pieces ({cartItems.length})</h3>
                                <div className="space-y-8">
                                    {cartItems.map((item) => (
                                        <div key={item.product} className="flex items-center gap-6 group">
                                            <div className="w-20 h-20 bg-white rounded-2xl overflow-hidden shrink-0 border border-gray-100 shadow-sm p-1">
                                                <img src={item.image} alt={item.name} className="w-full h-full object-cover rounded-xl" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-gray-900 font-serif font-bold text-lg leading-tight truncate">{item.name}</div>
                                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                                    {item.quantity} Unit · {item.weight} Grams · 22K Gold
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-gray-900 font-bold">
                                                    Rs {(calculateItemPrice(item) * item.quantity).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                </div>
                                                <div className="text-[9px] text-gray-400 uppercase tracking-widest mt-1">Total Piece</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Order Summary / Action */}
                        <div className="lg:sticky lg:top-32 h-fit space-y-6">
                            <div className="bg-gray-900 rounded-3xl p-10 text-white shadow-2xl shadow-gray-200">
                                <h2 className="text-2xl font-serif font-bold mb-8">Final Total</h2>
                                
                                <div className="space-y-4 pb-8 mb-8 border-b border-white/10">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-white/60 font-light tracking-wide uppercase text-[10px]">Collection Value</span>
                                        <span className="font-bold">Rs {totalPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-white/60 font-light tracking-wide uppercase text-[10px]">Shipping & Insurance</span>
                                        <span className="text-yellow-500 font-bold uppercase text-[10px]">Free</span>
                                    </div>
                                </div>

                                <div className="text-center mb-10">
                                    <div className="text-white/40 uppercase text-[9px] tracking-[0.2em] mb-2 font-bold leading-3">Approved Total Amount</div>
                                    <div className="text-4xl font-bold text-yellow-500">
                                        Rs {totalPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </div>
                                </div>

                                <button 
                                    onClick={handleClick}
                                    className="w-full bg-yellow-600 text-white py-5 rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:bg-white hover:text-gray-900 transition-all shadow-xl active:scale-95"
                                >
                                    Confirm & Place Order
                                </button>
                                
                                <div className="mt-8 text-center">
                                    <p className="text-[8px] text-white/30 uppercase tracking-[0.3em] font-medium italic">
                                        Secure Transaction Encrypted by 256-bit SSL
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}
